/**
 * Mappers: Convert between Supabase database rows and app-level TypeScript interfaces.
 * This keeps the existing component contracts stable while the data source changes from
 * static files to the database.
 */

import type {
  CharacterRow,
  ConversationRow,
  MessageRow,
  MemoryRow,
  ProfileRow,
  GalleryPostRow,
  BlogPostRow,
} from "./types"
import type { Character, CharacterPersonality } from "@/shared/types/character"
import type { CharacterLocalizationVariant } from "@/shared/types/character"
import type { Message, Conversation } from "@/shared/types/chat"
import type { Memory } from "@/shared/types/memory"
import type { BlogPost, StrapiBlock } from "@/shared/types/blog"
import type { User, SubscriptionTier } from "@/shared/types/user"
import { stripUnsafeMarkdownImages } from "@/shared/lib/blog-markdown-images"
import { defaultLocale, isSupportedBlogLocale } from "@/shared/lib/i18n/config"

// ── Profile → User ───────────────────────────────────────────────────────────

export function profileToUser(row: ProfileRow): User {
  return {
    id: row.id,
    email: row.email,
    name: row.display_name,
    avatar: row.avatar_url ?? undefined,
    subscription: row.subscription as SubscriptionTier,
    createdAt: new Date(row.created_at),
    favorites: [],
    createdCharacters: [],
  }
}

/** Extended profile data beyond the base User type */
export interface ProfileData extends User {
  username: string | null
  usernameChangedAt: string | null
  bio: string | null
  ageVerified: boolean
  tosAccepted: boolean
  onboardingCompleted: boolean
  chatDisplayName: string | null
  privacySettings: {
    show_profile: boolean
    allow_messages: boolean
    share_data: boolean
  }
  chatCoinsUsed: number
  imageGenerationsUsed: number
}

export function profileToProfileData(row: ProfileRow): ProfileData {
  return {
    ...profileToUser(row),
    username: row.username,
    usernameChangedAt: row.username_changed_at ?? null,
    bio: row.bio,
    ageVerified: row.age_verified,
    tosAccepted: row.tos_accepted,
    onboardingCompleted: row.onboarding_completed,
    chatDisplayName: row.chat_display_name,
    privacySettings: row.privacy_settings as ProfileData["privacySettings"],
    chatCoinsUsed: row.chat_coins_used,
    imageGenerationsUsed: row.image_generations_used,
  }
}

// ── Character Row → Character ────────────────────────────────────────────────

type CharacterCreatorProfile = {
  username: string | null
  display_name?: string | null
}

type CharacterRowWithCreatorProfile = CharacterRow & {
  profiles?: CharacterCreatorProfile | CharacterCreatorProfile[] | null
  creator_username?: string | null
  creator_display_name?: string | null
  localization?: Record<string, unknown> | null
  localization_source_fingerprint?: string | null
}

function getCreatorProfile(
  profiles: CharacterRowWithCreatorProfile["profiles"]
): CharacterCreatorProfile | null {
  if (Array.isArray(profiles)) return profiles[0] ?? null
  return profiles ?? null
}

function asStringArray(value: unknown): string[] | null {
  if (!Array.isArray(value)) return null
  const strings = value.filter((item): item is string => typeof item === "string")
  return strings.length === value.length ? strings : null
}

function mapCharacterLocalization(
  raw: Record<string, unknown> | null | undefined
): CharacterLocalizationVariant | null {
  if (!raw) return null
  const locale = typeof raw.locale === "string" ? raw.locale : null
  const status = typeof raw.status === "string" ? raw.status : null
  const sourceFingerprint =
    typeof raw.sourceFingerprint === "string" ? raw.sourceFingerprint : null

  if (!locale || !status || !sourceFingerprint) return null

  return {
    locale,
    status: status as CharacterLocalizationVariant["status"],
    sourceFingerprint,
    tagline: typeof raw.tagline === "string" ? raw.tagline : null,
    description: typeof raw.description === "string" ? raw.description : null,
    backstory: typeof raw.backstory === "string" ? raw.backstory : null,
    occupation: typeof raw.occupation === "string" ? raw.occupation : null,
    relationshipType:
      typeof raw.relationshipType === "string" ? raw.relationshipType : null,
    hobby: typeof raw.hobby === "string" ? raw.hobby : null,
    interests: asStringArray(raw.interests),
    reviewedAt: typeof raw.reviewedAt === "string" ? raw.reviewedAt : null,
    updatedAt: typeof raw.updatedAt === "string" ? raw.updatedAt : null,
  }
}

export function dbCharacterToCharacter(row: CharacterRowWithCreatorProfile): Character {
  const creatorProfile = getCreatorProfile(row.profiles)
  const localization = mapCharacterLocalization(row.localization)

  return {
    id: row.id,
    name: row.name,
    tagline: row.tagline,
    description: row.description,
    avatar: row.avatar,
    images: row.images,
    gender: row.gender,
    age: row.age,
    personality: row.personality as CharacterPersonality[],
    style: row.style,
    backstory: row.backstory,
    interests: row.interests,
    ethnicity: row.ethnicity,
    hairStyle: row.hair_style,
    hairColor: row.hair_color,
    eyeColor: row.eye_color,
    bodyType: row.body_type,
    breastSize: row.breast_size,
    buttSize: row.butt_size,
    firstMessage: row.first_message,
    scenario: row.scenario,
    voiceId: row.voice_asset_id ?? row.voice_id,
    voiceAssetId: row.voice_asset_id ?? null,
    loraAssetId: row.lora_asset_id ?? null,
    systemPrompt: row.system_prompt,
    occupation: row.occupation,
    relationshipType: row.relationship_type,
    hobby: row.hobby,
    fetish: row.fetish,
    skinTone: row.skin_tone,
    customPhysicalDetails: row.custom_physical_details,
    additionalPersonalityDetails: row.additional_personality_details,
    extraDetails: row.extra_details,
    messageCount: row.message_count,
    conversationCount: row.conversation_count,
    likes: row.likes_count,
    isPopular: row.is_popular || undefined,
    isNew: row.is_new || undefined,
    isOfficial: row.is_official || undefined,
    createdBy: row.created_by ?? undefined,
    creatorUsername: row.creator_username ?? creatorProfile?.username ?? undefined,
    creatorDisplayName:
      row.creator_display_name ?? creatorProfile?.display_name ?? undefined,
    localization,
    localizationSourceFingerprint: row.localization_source_fingerprint ?? null,
  }
}

// ── Message Row → Message ────────────────────────────────────────────────────

export function dbMessageToMessage(
  row: MessageRow,
  characterId: string
): Message {
  return {
    id: row.id,
    characterId,
    content: row.content,
    role: row.role as "user" | "assistant" | "system",
    timestamp: new Date(row.created_at),
    type: row.type as "text" | "image" | "system",
    mediaUrl: row.media_url ?? undefined,
    audioUrl: row.audio_url ?? undefined,
    metadata: row.metadata ?? undefined,
  }
}

// ── Memory Row → Memory ──────────────────────────────────────────────────────

export function dbMemoryToMemory(row: MemoryRow): Memory {
  return {
    id: row.id,
    characterId: row.character_id,
    content: row.content,
    source: row.source,
    sourceMessageId: row.source_message_id,
    createdAt: new Date(row.created_at),
  }
}

// ── Conversation Row → Conversation ──────────────────────────────────────────

export function dbConversationToConversation(
  row: ConversationRow & {
    characters?: { name: string; avatar: string } | null
  }
): Conversation {
  return {
    id: row.id,
    characterId: row.character_id,
    characterName: row.characters?.name ?? "Unknown",
    characterAvatar: row.characters?.avatar ?? "",
    lastMessage: row.last_message_content ?? "",
    lastMessageTime: new Date(row.last_message_at ?? row.created_at),
    unreadCount: row.unread_count,
    messageCount: row.message_count,
    messages: [], // Messages are loaded separately via useMessages()
  }
}

// ── Gallery Post Row → GalleryPost ───────────────────────────────────────────

export interface GalleryPostGenerationParams {
  generation_id?: string
  prompt?: string
  enhancedPrompt?: string
  style?: string
  aspectRatio?: string
  guidanceScale?: number
  negativePrompt?: string
  import_source?: string
  import_source_id?: string
  import_score?: number
}

export interface GalleryPost {
  id: string
  userId: string
  username: string
  userAvatar: string
  type: "image" | "video"
  mediaUrl: string
  thumbnailUrl?: string
  aspectRatio: "square" | "portrait" | "landscape"
  caption: string
  likes: number
  comments: number
  tags: string[]
  createdAt: string
  isLiked?: boolean
  batchCount?: number
  batchImages?: GalleryPost[]
  generationParams?: GalleryPostGenerationParams
}

export function dbGalleryPostToGalleryPost(
  row: GalleryPostRow & {
    profiles?:
      | { username: string | null; avatar_url: string | null }
      | { username: string | null; avatar_url: string | null }[]
      | null
  },
  isLiked?: boolean
): GalleryPost {
  const genParams = row.generation_params as GalleryPostGenerationParams | null
  const profile = Array.isArray(row.profiles) ? row.profiles[0] : row.profiles

  return {
    id: row.id,
    userId: row.user_id,
    username: profile?.username ?? "anonymous",
    userAvatar: profile?.avatar_url ?? "",
    type: row.type,
    mediaUrl: row.media_url,
    thumbnailUrl: row.thumbnail_url ?? undefined,
    aspectRatio: row.aspect_ratio,
    caption: row.caption ?? "",
    likes: row.likes_count,
    comments: row.comments_count,
    tags: row.tags,
    createdAt: row.created_at,
    isLiked,
    generationParams: genParams ?? undefined,
  }
}

// ── Blog Post Row → BlogPost ────────────────────────────────────────────────

function flattenBlocksToContent(blocks: Record<string, unknown>[]): string {
  return blocks
    .filter((b) => b.__component === "shared.rich-text" && typeof b.body === "string")
    .map((b) => b.body as string)
    .join("\n\n")
}

function wordCount(text: string): number {
  return text.split(/\s+/).filter(Boolean).length
}

function sanitizeBlogBlocks(blocks: StrapiBlock[]): StrapiBlock[] {
  return blocks.map((block) => {
    if (block.__component === "shared.rich-text") {
      return {
        ...block,
        body: stripUnsafeMarkdownImages(block.body),
      }
    }
    return block
  })
}

export function dbBlogPostToBlogPost(row: BlogPostRow): BlogPost {
  const blocks = sanitizeBlogBlocks((row.content ?? []) as StrapiBlock[])
  const content = flattenBlocksToContent(blocks as Record<string, unknown>[])
  const readTime = Math.max(1, Math.ceil(wordCount(content) / 200))
  const locale = isSupportedBlogLocale(row.locale) ? row.locale : defaultLocale

  return {
    documentId: row.document_id,
    locale,
    slug: row.slug,
    title: row.title,
    excerpt: row.description ?? "",
    content,
    blocks,
    coverImage: row.cover_image_url ?? "",
    coverImageAlt: row.cover_image_alt ?? row.title,
    category: row.category ?? "",
    author: row.author_name ?? "",
    publishedAt: row.published_at ?? row.created_at,
    updatedAt: row.strapi_updated_at ?? row.updated_at,
    readTime: row.read_time > 0 ? row.read_time : readTime,
    tags: row.category ? [row.category] : [],
    isExplicit: row.is_explicit,
  }
}
